import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Clock, ChevronRight, Gamepad2, Mail, Zap } from '../lib/icons';
import { BLOG_POSTS, CATEGORIES } from '../data/blog-posts';
import SEOHead from '../components/SEOHead';
import ImageSlot from '../components/ImageSlot';

const BlogIndexPage = () => {
  const [activeCategory, setActiveCategory] = useState('all');

  const filtered =
    activeCategory === 'all'
      ? BLOG_POSTS
      : BLOG_POSTS.filter(p => p.categorySlug === activeCategory);

  const featured = BLOG_POSTS[0];
  const rest = filtered.filter(p => p.slug !== featured.slug);

  const blogJsonLD = {
    '@context': 'https://schema.org',
    '@type': 'Blog',
    name: 'GameFix Solutions — Controller Upgrade & Repair Blog',
    description:
      'Expert guides on PS5 stick drift, TMR hall-effect joystick upgrades, mouse click triggers, back paddles, and custom DualSense controllers.',
    url: 'https://gamefixsolutions.com/blog',
    publisher: {
      '@type': 'Organization',
      name: 'GameFix Solutions',
      url: 'https://gamefixsolutions.com',
    },
  };

  return (
    <div className="bg-[#0D0D0D] min-h-screen">
      <SEOHead
        title="Controller Upgrade & PS5 Repair Blog | GameFix Solutions"
        description="Expert guides on PS5 stick drift fixes, TMR hall-effect joystick upgrades, mouse click triggers, back paddles, and custom DualSense builds. Written by technicians who do this daily."
        keywords="ps5 controller blog, ps5 stick drift fix, tmr joystick upgrade, custom dualsense, ps5 repair guide"
        canonical="https://gamefixsolutions.com/blog"
        jsonLD={blogJsonLD}
      />

      {/* Hero */}
      <section className="relative bg-gradient-to-b from-black via-[#0D0D0D] to-[#0D0D0D] py-20 overflow-hidden border-b border-gray-900">
        <div className="absolute inset-0 opacity-10 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNiIgc3Ryb2tlPSIjMzlGRjE0IiBzdHJva2Utd2lkdGg9IjAuNSIvPjwvZz48L3N2Zz4=')]" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-4">
            <div className="inline-flex items-center gap-2 bg-[#39FF14]/10 border border-[#39FF14]/30 rounded-full px-5 py-1.5 mb-6">
              <Zap className="h-4 w-4 text-[#39FF14]" />
              <span className="text-[#39FF14] font-semibold text-sm">CONTROLLER KNOWLEDGE BASE</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-[#E0E0E0] mb-4">
              Custom Controller Guides
            </h1>
            <p className="text-xl text-[#B0B0B0] max-w-2xl mx-auto">
              Written by the technicians who build and upgrade PS5 controllers every day.
              TMR joysticks, mouse click triggers, stick drift fixes, and more.
            </p>
          </div>
        </div>
      </section>

      {/* Featured Post */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <p className="text-xs font-semibold text-[#B0B0B0] uppercase tracking-widest mb-6">Featured</p>
        <Link
          to={`/blog/${featured.slug}`}
          className="group grid grid-cols-1 lg:grid-cols-2 gap-0 bg-gray-900 rounded-2xl border border-gray-800 hover:border-[#39FF14]/50 overflow-hidden transition-all duration-300"
        >
          {/* Cover photo or gradient fallback */}
          <div className="aspect-video lg:aspect-auto min-h-[240px] relative overflow-hidden">
            <ImageSlot
              slotId={`blog-cover-${featured.slug}`}
              fallbackSrc={featured.image ?? ''}
              fallbackAlt={featured.title}
              className="object-cover"
              wrapperClassName="w-full h-full"
            />
            {/* Category badge overlay */}
            <div className="absolute bottom-0 left-0 right-0 p-8 bg-gradient-to-t from-black/80 to-transparent">
              <span
                className="inline-block text-xs font-bold px-3 py-1 rounded-full mb-3"
                style={{ backgroundColor: `${featured.categoryColor}22`, color: featured.categoryColor, border: `1px solid ${featured.categoryColor}44` }}
              >
                {featured.category}
              </span>
              <h2 className="text-2xl lg:text-3xl font-bold text-[#E0E0E0] leading-snug group-hover:text-[#39FF14] transition-colors">
                {featured.title}
              </h2>
            </div>
          </div>
          <div className="p-8 flex flex-col justify-between">
            <p className="text-[#B0B0B0] leading-relaxed text-base mb-6">{featured.excerpt}</p>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 text-sm text-gray-600">
                <Clock className="h-4 w-4" />
                <span>{featured.readTime}</span>
              </div>
              <span className="flex items-center gap-1 text-[#39FF14] font-semibold text-sm group-hover:gap-2 transition-all">
                Read Article <ChevronRight className="h-4 w-4" />
              </span>
            </div>
          </div>
        </Link>
      </section>

      {/* Category Filter */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-6">
        <div className="flex flex-wrap gap-2">
          {CATEGORIES.map(cat => (
            <button
              key={cat.slug}
              onClick={() => setActiveCategory(cat.slug)}
              className={`px-4 py-2 rounded-full text-sm font-semibold border transition-all ${
                activeCategory === cat.slug
                  ? 'bg-[#39FF14] text-black border-[#39FF14]'
                  : 'text-[#B0B0B0] border-gray-700 hover:border-gray-600 hover:text-[#E0E0E0]'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>
      </section>

      {/* Post Grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {(activeCategory === 'all' ? rest : filtered).map(post => (
            <Link
              key={post.slug}
              to={`/blog/${post.slug}`}
              className="group bg-gray-900 rounded-2xl border border-gray-800 hover:border-[#39FF14]/40 overflow-hidden transition-all duration-300 flex flex-col"
            >
              <div
                className="h-36 relative overflow-hidden"
                style={{ background: `linear-gradient(135deg, ${post.categoryColor}20 0%, #111 100%)` }}
              >
                <ImageSlot
                  slotId={`blog-cover-${post.slug}`}
                  fallbackSrc={post.image ?? ''}
                  fallbackAlt={post.title}
                  className="object-cover"
                  wrapperClassName="absolute inset-0"
                />
                <div className="absolute bottom-0 left-0 right-0 px-5 py-3 bg-gradient-to-t from-black/70 to-transparent pointer-events-none z-[15]">
                  <span
                    className="text-xs font-bold px-2.5 py-1 rounded-full"
                    style={{ backgroundColor: `${post.categoryColor}20`, color: post.categoryColor, border: `1px solid ${post.categoryColor}40` }}
                  >
                    {post.category}
                  </span>
                </div>
              </div>
              <div className="p-6 flex flex-col flex-1">
                <h3 className="text-[#E0E0E0] font-bold text-lg leading-snug mb-3 group-hover:text-[#39FF14] transition-colors">
                  {post.title}
                </h3>
                <p className="text-[#B0B0B0] text-sm leading-relaxed flex-1 mb-4 line-clamp-3">
                  {post.excerpt}
                </p>
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2 text-gray-600">
                    <Clock className="h-3.5 w-3.5" />
                    <span>{post.readTime}</span>
                  </div>
                  <span className="flex items-center gap-1 text-[#39FF14] font-semibold group-hover:gap-2 transition-all">
                    Read <ChevronRight className="h-3.5 w-3.5" />
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="text-center py-20 text-[#B0B0B0]">
            No posts in this category yet — check back soon.
          </div>
        )}
      </section>

      {/* CTA Banner */}
      <section className="bg-gradient-to-r from-gray-900 to-black border-y border-gray-800 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Gamepad2 className="h-10 w-10 text-[#39FF14] mx-auto mb-4" />
          <h2 className="text-3xl font-bold text-[#E0E0E0] mb-3">
            Ready to Upgrade Your Controller?
          </h2>
          <p className="text-[#B0B0B0] mb-8 max-w-xl mx-auto">
            Everything in these guides is work we do every day — locally in San Diego and Temecula, or by mail from anywhere in the US.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/custom-controllers"
              className="inline-flex items-center justify-center gap-2 bg-[#39FF14] hover:bg-[#00BFFF] text-black font-bold px-8 py-3 rounded-lg transition-all"
            >
              <Gamepad2 className="h-5 w-5" /> Build My Controller
            </Link>
            <Link
              to="/mail-in-repairs"
              className="inline-flex items-center justify-center gap-2 bg-gray-800 hover:bg-gray-700 text-[#E0E0E0] font-semibold px-8 py-3 rounded-lg border border-gray-700 hover:border-[#39FF14] transition-all"
            >
              <Mail className="h-5 w-5" /> Mail-In Upgrade
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogIndexPage;
